import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Form, Input, Modal, Switch, message, TreeSelect } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import './styleUpdateModalUser.css';
import { useGroupsStore } from '../../../../store/groups/Groups';
import { Group } from '../../../../types/Group';
import { useAuthStore } from '../../../../store/auth/auth';
import { getCreateUser } from '../../../../api/users/getCreateUser';
import { User } from '../../../../types/User';
import { getEditUser } from 'api/users/updateUser';

interface NewGroupModalProps {
    open: boolean;
    onOk: () => void;
    onCancel: () => void;
    selectedUser: User | null;
}
const NewUserModal: React.FC<NewGroupModalProps> = ({ open, onOk, onCancel, selectedUser }) => {
    const { groups, fetchGroups } = useGroupsStore();
    const { user, SmartDVRToken } = useAuthStore();
    const [form] = Form.useForm();
    const [showDeleteWarning, setShowDeleteWarning] = useState<boolean>(false);
    const [pendingValues, setPendingValues] = useState<unknown>(null);

    useEffect(() => {
        if (groups.length === 0) {
            fetchGroups();
        }
    }, [groups.length, fetchGroups]);

    // Объединяем группы и подгруппы в один массив
    const getAllSubGroups = useCallback((group: Group): Group[] => {
        const subGroups: Group[] = [];
        group.sub_groups.forEach((subGroup) => {
            subGroups.push(subGroup);
            if (subGroup.sub_groups) {
                subGroups.push(...getAllSubGroups(subGroup));
            }
        });
        return subGroups;
    }, []);

    const allGroups: Group[] = useMemo(() => {
        const result: Group[] = [];
        groups.forEach((group) => {
            result.push(group);
            if (group.sub_groups) {
                result.push(...getAllSubGroups(group));
            }
        });
        return result;
    }, [groups, getAllSubGroups]);

    const convertGroupsToTreeData = useCallback((groups: Group[]): any[] => {
        return groups
            .filter((group) =>
                group.uid !== '00000000-0000-0000-0000-000000000002' &&
                group.uid !== '00000000-0000-0000-0000-000000000003' &&
                group.name !== 'Несортированные'
            )
            .map((group) => ({
                title: group.name,
                value: group.uid,
                key: group.uid,
                children: group.sub_groups ? convertGroupsToTreeData(group.sub_groups) : undefined,
            }));
    }, []);

    // Фильтрация групп по иерархии залогиненного пользователя
    const loggedUserGroupUids = useMemo(() => user?.groups
        ? (user.groups as any[]).map(g => typeof g === 'string' ? g : g.uid)
        : [], [user]);

    const collectSubGroups = useCallback((group: Group, acc: Set<string>): void => {
        acc.add(group.uid);
        group.sub_groups?.forEach(sub => {
            collectSubGroups(sub, acc);
        });
    }, []);

    const getAvailableGroups = useCallback((userGroupUids: string[], allGroups: Group[]): Group[] => {
        const availableUids = new Set<string>();
        const groupMap = new Map(allGroups.map(g => [g.uid, g]));

        userGroupUids.forEach(uid => {
            const group = groupMap.get(uid);
            if (group) {
                collectSubGroups(group, availableUids);
            }
        });

        return allGroups.filter(g => availableUids.has(g.uid));
    }, [collectSubGroups]);

    const filteredGroups = useMemo((): Group[] => {
        if (!user) return [];
        if (user.isAdmin) return allGroups;
        return getAvailableGroups(loggedUserGroupUids, allGroups);
    }, [user, allGroups, getAvailableGroups, loggedUserGroupUids]);

    const treeData = useMemo(() => {
        if (user?.isAdmin) return convertGroupsToTreeData(groups);

        const availableUids = new Set(filteredGroups.map((group) => group.uid));
        const rootGroups = filteredGroups.filter((group) => !availableUids.has(group.parent_uid));
        return convertGroupsToTreeData(rootGroups);
    }, [user, groups, filteredGroups, convertGroupsToTreeData]);

    useEffect(() => {
        if (selectedUser && open) {
            form.setFieldsValue({
                name: selectedUser.name || '',
                login: selectedUser.login || '',
                password: '',
                isAdmin: selectedUser.isAdmin ?? false,
                parent_uid: Array.isArray(selectedUser.groups)
                    ? (selectedUser.groups.length > 0 && typeof selectedUser.groups[0] === 'object' && 'uid' in selectedUser.groups[0]
                        ? (selectedUser.groups as { uid: string }[]).map(g => g.uid)
                        : selectedUser.groups as unknown as string[])
                    : [],
            });
        }
    }, [selectedUser, open, form]);

    const saveUser = useCallback((values: any): void => {
        if (user) {
            const userData = {
                ...(selectedUser?.uid ? { uid: selectedUser.uid } : {}),
                name: values.name,
                login: values.login,
                ...(values.password ? { password: values.password } : {}),
                isAdmin: values.isAdmin,
                groups: values.parent_uid || [],
            };
            getEditUser(SmartDVRToken, user.login, userData)
                .then((response) => {
                    if (response.success) {
                        message.success('Пользователь успешно изменён!');
                        onOk();
                    } else {
                        message.error(`Ошибка при редактировании пользователя: ${response.error.error}`);
                    }
                })
                .catch(() => {
                    message.error('Ошибка при редактировании пользователя');
                });
        }
    }, [user, selectedUser, SmartDVRToken, onOk]);

    const handleConfirmDelete = useCallback((): void => {
        if (pendingValues) {
            saveUser(pendingValues);
        }
        setShowDeleteWarning(false);
        setPendingValues(null);
    }, [pendingValues, saveUser]);

    const handleCancelDelete = useCallback((): void => {
        setShowDeleteWarning(false);
        setPendingValues(null);
    }, []);

    const handleOk = useCallback((): void => {
        form.validateFields().then(values => {
            if (values.parent_uid.length === 0) {
                setPendingValues(values);
                setShowDeleteWarning(true);
                return;
            }
            saveUser(values);
        }).catch((errorInfo) => {
            console.log('Validation failed:', errorInfo);
        });
    }, [form, saveUser]);

    const handleCancel = useCallback((): void => {
        form.resetFields();
        onCancel();
    }, [form, onCancel]);

    return (
        <>
            <Modal
                open={showDeleteWarning}
                onOk={handleConfirmDelete}
                onCancel={handleCancelDelete}
                title="Внимание"
                okText="Удалить пользователя"
                cancelText="Отмена"
                okButtonProps={{
                    style: { backgroundColor: '#DE7171', borderColor: '#DE7171', color: '#fff' },
                }}
                cancelButtonProps={{
                    style: { backgroundColor: '#ffffff', color: '#DE7171', borderColor: '#DE7171' },
                    className: 'custom-cancel-btn'
                }}
            >
                Если вы удалите все группы у пользователя, он будет полностью удалён из системы. Продолжить?
            </Modal>
            <Modal
                title={
                    <div className="titleModal_Group">
                        <span className="spanGroup">Редактировать пользователя</span>
                        <CloseOutlined onClick={handleCancel} className="closeBut_Group" />
                    </div>
                }
                className="newGroup_modal"
                open={open}
                onOk={handleOk}
                onCancel={handleCancel}
                width={720}
                closable={false}
                footer={
                    <Button className="buttonAdd" onClick={handleOk}>
                        Сохранить
                    </Button>
                }
            >
                <Form form={form} >
                    <Form.Item
                        className="form_Group"
                        label={<span className="inputLabel_Group">Имя</span>}
                        name="name"
                        required={true}
                        rules={[
                            {
                                required: true,
                                message: 'Пожалуйста, введите имя',
                            }
                        ]}
                    >
                        <Input className="input_Group" />
                    </Form.Item>
                    <Form.Item
                        className="form_Group"
                        label={<span className="inputLabel_Group">Группа</span>}
                        name="parent_uid"
                        required={false}
                    >
                        <TreeSelect
                            className="selectGroup"
                            placeholder="Введите название или выберите из списка"
                            treeData={treeData}
                            showSearch
                            allowClear={false}
                            treeDefaultExpandAll
                            multiple={true}
                            filterTreeNode={(inputValue, treeNode) => {
                                const title = String(treeNode.title || '');
                                return title.toLowerCase().includes(inputValue.toLowerCase());
                            }}
                            treeNodeFilterProp="title"
                        />
                    </Form.Item>
                    <Form.Item
                        className="form_Group"
                        label={<span className="inputLabel_Group">Логин</span>}
                        name="login"
                        required={true}
                        rules={[
                            {
                                required: true,
                                message: 'Пожалуйста, введите логин',
                            }
                        ]}
                    >
                        <Input className="input_Group" />
                    </Form.Item>
                    <Form.Item
                        className="form_Group"
                        label={<span className="inputLabel_Group">Администратор</span>}
                        name="isAdmin"
                        valuePropName="checked"
                        required={false}
                    >
                        <Switch />
                    </Form.Item>
                    <Form.Item
                        className="form_Group"
                        label={<span className="inputLabel_Group">Пароль</span>}
                        name="password"
                        required={false}
                    >
                        <Input className="input_Group" />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};
export default NewUserModal;
